import { BanknotesIcon, BuildingOfficeIcon, CreditCardIcon, GlobeAltIcon, UserIcon } from "@heroicons/react/20/solid";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { clearLoadingData, setActionLoading } from "../../../redux/reducers/loadingReducer";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import AccountTab from "./AccountTab";
import BillingTab from "./BillingTab";
import ClubTab from "./ClubTab";
import PaymentsTab from "./PaymentsTab";
import WebsiteWidgetsPage from "../website/WebsiteWidgetsPage";

/**
 * Account settings (new design) — replaces settings/myAccount/MyAccount.
 * Settings (called "Account settings" until 2026-11-05).
 * Routes: /my-account, /my-account/company, /my-account/online-payment, /my-account/widgets, /my-account/billing.
 * Widgets (2026-11-05): the website blocks editor, was the "Website & webshop" menu item / page (/website, now redirects
 * here); the page is wider on that tab because the editor has a live preview.
 * Same tab icons as before. Refreshes the user (GET users/getUserInfo) on open, as before.
 */

const TABS = [
  { key: "account", label: "Account", icon: UserIcon, path: "/my-account" },
  { key: "company", label: "Club", icon: BuildingOfficeIcon, path: "/my-account/company" },
  { key: "online-payment", label: "Online payments", icon: BanknotesIcon, path: "/my-account/online-payment" },
  { key: "widgets", label: "Widgets", icon: GlobeAltIcon, path: "/my-account/widgets" },
  { key: "billing", label: "Billing", icon: CreditCardIcon, path: "/my-account/billing" },
];

export default function AccountSettingsPage({ selectedTab = "account" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userReduxState = useSelector(userState);
  const tab = TABS.some((t) => t.key === selectedTab) ? selectedTab : "account";

  React.useEffect(() => {
    dispatch(clearLoadingData());
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
        if (res?.data?.statusCode === 200) {
          dispatch(userActions.setUserData({ appUrl: userReduxState.userData?.appUrl, ...res.data.data }));
        }
      } catch (e) {
        /* keep what we have */
      }
    })();
    return () => {
      dispatch(setActionLoading(false));
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className={classNames("mx-auto", tab === "widgets" ? "max-w-7xl" : "max-w-4xl")}>
            <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">Settings</h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">Your login, club profile, online payments, website widgets and FitClub plan.</p>

            <div role="tablist" aria-label="Settings" className="-mx-4 mt-5 flex gap-1 overflow-x-auto border-b border-gray-200 px-4 sm:mx-0 sm:px-0">
              {TABS.map((t) => {
                const on = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => !on && navigate(t.path, { replace: true })}
                    className={classNames(
                      "-mb-px flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-b-[3px] px-3 text-[15px] font-extrabold sm:px-4",
                      on ? "border-portland-orange text-brown-coffee" : "border-transparent text-gray-500 hover:text-gray-900"
                    )}
                  >
                    <t.icon aria-hidden="true" className={classNames("size-5 shrink-0", on ? "text-portland-orange" : "text-gray-400")} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-6" role="tabpanel">
              {tab === "account" && <AccountTab />}
              {tab === "company" && <ClubTab />}
              {tab === "online-payment" && <PaymentsTab />}
              {tab === "widgets" && <WebsiteWidgetsPage embedded />}
              {tab === "billing" && <BillingTab />}
            </div>
          </div>
        </div>
      }
    />
  );
}
